import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ModalRequestDetailsBaseComponent } from '../_modal-request-details-base/modal-request-details-base.component';
import { BehaviorSubject } from 'rxjs';
import { RegisterRequisition, ReplyConfig, REQUEST_TYPE } from '../../_core/approvals-page.model';
import { AlertComponent } from '../../../../../components/alert/alert.component';
import { LocalLoaderComponent } from '../../../../../components/loader/local-loader/local-loader.component';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormErrorsModule } from '@spartacus/storefront';
import { ToasterService } from '../../../../../components/toaster/toaster.service';
import { ModalService } from '../../../../../components/modal-list/modal-list.service';
import { ApprovalsPageService } from '../../_core/approvals-page.service';
import { CustomValidatorService } from '../../../../../components/form-elements/custom-validator.service';
import { Country, I18nModule, UserAddressService } from '@spartacus/core';
import { FormContactComponent } from '../../../user/profile/components/register/components/profile-details/components/form-contact/form-contact.component';
import { FormAddressComponent } from '../../../user/profile/components/register/components/form-address/form-address.component';
import { Option } from '../../../../../core/models/option.model';
import { FormAccountDetailsComponent } from './form-account-details/form-account-details.component';
import { ModalRequestActionsComponent } from '../_modal-request-actions/modal-request-actions.component';
import { FormRolesComponent } from './form-roles/form-roles.component';
import { FormAccountNumbersComponent } from './form-account-numbers/form-account-numbers.component';
import { UserRegisterRequest } from '../../../user/core/user-aux.model';

@Component({
  selector: 'modal-register-details',
  standalone: true,
  imports: [
    CommonModule,
    AlertComponent,
    LocalLoaderComponent,
    I18nModule,
    ReactiveFormsModule,
    FormErrorsModule,
    FormContactComponent,
    FormAddressComponent,
    FormAccountDetailsComponent,
    FormAccountNumbersComponent,
    FormRolesComponent,
    I18nModule,
    ModalRequestActionsComponent,
  ],
  templateUrl: './modal-register-details.component.html',
  styleUrl: './modal-register-details.component.scss',
})
export class ModalRegisterDetailsComponent extends ModalRequestDetailsBaseComponent implements OnInit {
  registerRequisition$ = new BehaviorSubject<RegisterRequisition>({});

  profileDetailsForm: FormGroup = this.fb.group({
    firstName: ['', [this.customValidatorService.required()]],
    lastName: ['', [this.customValidatorService.required()]],
    jobTitle: ['', [this.customValidatorService.required()]],
    email: ['', [this.customValidatorService.isEmail()]],
    dialingCode: ['', [this.customValidatorService.required()]],
    dialingIsoCode: ['', [this.customValidatorService.required()]],
    phoneNumber: ['', [this.customValidatorService.required()]],
    extension: ['', []],
    optIn: [false, []],

    companyName: ['', [this.customValidatorService.required()]],
    street: ['', [this.customValidatorService.required()]],
    country: ['', [this.customValidatorService.required()]],
    region: ['', [this.customValidatorService.required()]],
    city: ['', [this.customValidatorService.required()]],
    zipCode: ['', [this.customValidatorService.required()]],
  });

  accountDetailsForm: FormGroup = this.fb.group({
    hasAccount: [undefined, [this.customValidatorService.required()]],
    accountNumber: ['', []],
    billingAddressIsEqualWorkAddress: [undefined, []],

    companyName: ['', []],
    street: ['', []],
    country: ['', []],
    region: ['', []],
    city: ['', []],
    zipCode: ['', []],

    linkedAccounts: ['', []],
    productCategory: ['', []],
    agreeWithPolicy: [false, [this.customValidatorService.required(), Validators.requiredTrue]],
  });

  formRole: FormGroup = this.fb.group({
    role: ['', [this.customValidatorService.required()]],
    givenRole: ['', []],
    agreement: [false, []],
  });

  formAccountNumber: FormGroup = this.fb.group({
    accountNumber: ['', [this.customValidatorService.required()]],
  });

  phoneCountry?: Country;
  countries$ = new BehaviorSubject<Country[]>([]);
  stateRegions$ = new BehaviorSubject<Option[]>([]);
  linkedAccounts$ = new BehaviorSubject<string[]>([]);
  accountNumbers$ = new BehaviorSubject<string[]>([]);
  profileInfo$ = new BehaviorSubject<UserRegisterRequest | undefined>({});
  replyConfig: ReplyConfig = {
    type: REQUEST_TYPE.REGISTER,
    registerData: {},
  };

  intialQtyAccounts = 0;

  constructor(
    protected _toasterService: ToasterService,
    protected _modalService: ModalService,
    protected _approvalsPageService: ApprovalsPageService,
    protected _cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    private customValidatorService: CustomValidatorService,
    private userAddressService: UserAddressService,
  ) {
    super(_toasterService, _modalService, _approvalsPageService, _cdr);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.getRegisterRequisition();
    this.fetchCountries();
  }

  getRegisterRequisition() {
    this.subscription.add(
      this.requisitionDetailsRequest$.subscribe(response => {
        this.registerRequisition$.next(response.registerRequest || {});

        response.hasSuccess && this.populateForm();
      }),
    );
  }

  fetchCountries(): void {
    this.userAddressService.getDeliveryCountries().subscribe(data => {
      this.countries$.next(data);
    });

    this.userAddressService.loadDeliveryCountries();
  }

  populateForm() {
    const registerRequisition = this.registerRequisition$.value;
    const profileInfo = registerRequisition.registrationRequest;

    this.profileDetailsForm.patchValue({
      firstName: profileInfo?.firstName,
      lastName: profileInfo?.lastName,
      jobTitle: profileInfo?.jobTitle,
      email: profileInfo?.email,
      dialingCode: profileInfo?.dialingCode,
      dialingIsoCode: profileInfo?.dialingIsoCode,
      phoneNumber: profileInfo?.phoneNumber,
      extension: profileInfo?.extension,
      optIn: profileInfo?.optIn,

      companyName: profileInfo?.contactInformation?.companyName,
      street: profileInfo?.contactInformation?.street,
      country: profileInfo?.contactInformation?.country,
      region: profileInfo?.contactInformation?.region,
      city: profileInfo?.contactInformation?.city,
      zipCode: profileInfo?.contactInformation?.zipCode,
    });

    this.accountDetailsForm.patchValue({
      hasAccount: profileInfo?.hasAccount,
      billingAddressIsEqualWorkAddress: profileInfo?.billingAddressIsEqualWorkAddress ?? true,
      accountNumber: (profileInfo?.accountNumber && profileInfo?.accountNumber[0]) || '',

      companyName: profileInfo?.billingAddress?.companyName,
      street: profileInfo?.billingAddress?.street,
      country: profileInfo?.billingAddress?.country,
      region: profileInfo?.billingAddress?.region,
      city: profileInfo?.billingAddress?.city,
      zipCode: profileInfo?.billingAddress?.zipCode,

      productCategory: profileInfo?.productCategory,
      agreeWithPolicy: profileInfo?.agreeWithPolicy,
    });

    this.formRole.patchValue({
      givenRole: profileInfo?.givenRole,
    });

    if (profileInfo?.givenRole) {
      this.formRole.disable();
    }

    this.intialQtyAccounts = profileInfo?.linkedAccounts?.length || 0;
    this.linkedAccounts$.next(profileInfo?.linkedAccounts || []);
    this.setAccountNumbers(profileInfo?.accountNumber || []);

    if (profileInfo?.linkedAccounts?.length) {
      this.formRole.get('agreement')?.setValidators([Validators.requiredTrue]);
    }

    this.profileInfo$.next(profileInfo);
    this.profileDetailsForm.disable();
    this.accountDetailsForm.disable();
  }

  setAccountNumbers(accountNumbers: string[]) {
    this.accountNumbers$.next(accountNumbers);
  }

  validation(): boolean {
    this.formRole.markAllAsTouched();
    this.toasterService.closeALL();

    const accountNumbers = this.accountNumbers$.value;
    const linkedAccounts = this.linkedAccounts$.value;
    let isOk = true;

    if (accountNumbers.length === 0) {
      this.toasterService.displayError('Please add at least one validated account number');
      return false;
    }

    if (linkedAccounts.length && this.formRole.get('agreement')?.value === false) {
      this.toasterService.displayError('Please agree with the terms and conditions');
      this.formRole.get('agreement')?.setErrors({ required: true });
      return false;
    }

    isOk = isOk && this.formRole.valid;

    this.replyConfig.registerData = {
      role: this.formRole.get('role')?.value,
      accountNumbers: accountNumbers,
    };

    return isOk;
  }
}
